import { getInitials } from '@ardoq/common-helpers';
import { colors, spacing } from '@ardoq/design-tokens';
import { Icon, IconName, IconSize } from '@ardoq/icons';
import { FlexBox, type SizeVariant } from '@ardoq/layout';
import { getDecoratorIconSize } from './atoms';
import { useState } from 'react';
import styled, { css } from 'styled-components';

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

/**
 * Circle with a user's initials, in a table row or list item.
 *
 * Accessibility note: marked aria-hidden="true" because:
 * - The initials are decorative and provide no unique information
 * - The full user name is rendered next to the avatar
 *
 * Colors meet WCAG AA contrast requirements (4.5:1) for normal text, ensuring
 * the initials stay readable for users with low vision.
 */
const InitialsCircle = styled.div.attrs({
  'aria-hidden': 'true',
})`
  box-sizing: border-box;
  color: ${colors.textModerate};
  background: transparent;
  border: 1px solid ${colors.borderNeutral};
  height: ${spacing.s24};
  width: ${spacing.s24};
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  display: flex;
  overflow: hidden;
  flex-shrink: 0;
  font-size: 10px;
`;

/**
 * A user's profile image, in a table row or list item.
 *
 * Accessibility note: marked aria-hidden="true" because the image is
 * decorative and the full user name is rendered next to it.
 */
const ImageCircle = styled.img.attrs({
  'aria-hidden': 'true',
  alt: '',
})`
  height: ${spacing.s24};
  width: ${spacing.s24};
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

/**
 * Circle behind a user's profile image, initials, or an overflow count in an
 * overlapping stack of users.
 *
 * Accessibility note: marked aria-hidden="true" because:
 * - The image and the initials are decorative and provide no unique information
 * - A stack renders no names, so the full user name is available through the
 *   tooltip instead
 *
 * Colors meet WCAG AA contrast requirements (4.5:1) for normal text, ensuring
 * the initials stay readable for users with low vision.
 */
const StackedAvatarCircle = styled.div.attrs({
  'aria-hidden': 'true',
})`
  color: ${colors.grey600};
  background: ${colors.grey200};
  height: ${spacing.s24};
  width: ${spacing.s24};
  border-radius: 50%;
  font-size: 9px;
  align-items: center;
  justify-content: center;
  display: flex;
  overflow: hidden;
  user-select: none;
  border: 1px solid ${colors.white950};
  &:not(:first-of-type) {
    margin-left: -${spacing.s4};
  }
`;

/**
 * Circle behind the profile image or initials of the user a surface is about,
 * sized to sit beside a page heading or a card footer.
 *
 * Accessibility note: marked aria-hidden="true" because:
 * - The image and the initials are decorative and provide no unique information
 * - The heading or footer next to the avatar names the user
 *
 * Colors meet WCAG AA contrast requirements (4.5:1) for normal text, ensuring
 * the initials stay readable for users with low vision.
 */
const ProfileAvatarCircle = styled.div.attrs({
  'aria-hidden': 'true',
})`
  color: ${colors.textModerate};
  background: ${colors.grey200};
  height: ${spacing.s32};
  width: ${spacing.s32};
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  display: flex;
  overflow: hidden;
  flex-shrink: 0;
`;

/**
 * Profile image sized to stand in for a decorator's leading icon.
 *
 * Accessibility note: marked aria-hidden="true" because it replaces the
 * decorative person icon and carries no information the name or email beside
 * it does not already give.
 */
const InlineAvatarImage = styled.img`
  height: ${IconSize.MEDIUM}px;
  width: ${IconSize.MEDIUM}px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

type UserAvatarProps = {
  name: string;
  image: string | undefined;
};

const AvatarContent = ({ name, image }: UserAvatarProps) => {
  const [failedImage, setFailedImage] = useState<string | null>(null);

  if (!image || image === failedImage) return <>{getInitials(name)}</>;

  return (
    <AvatarImage alt="" src={image} onError={() => setFailedImage(image)} />
  );
};

/**
 * Profile image for a single user, falling back to initials, sized for a table
 * row or a list item. Use it where the user's name is rendered next to the
 * avatar.
 *
 * The image is not resolved for every user, and the URLs the API returns are
 * not guaranteed to load, so initials remain the fallback both when no image
 * is set and when the image fails to load.
 *
 * Accessibility note: marked aria-hidden because the image and the initials
 * are decorative — they carry no information the adjacent name does not
 * already give. Colors meet WCAG AA contrast (4.5:1) so the initials stay
 * readable for users with low vision.
 */
export const UserAvatar = ({ name, image }: UserAvatarProps) => {
  const [failedImage, setFailedImage] = useState<string | null>(null);

  if (!image || image === failedImage) {
    return <InitialsCircle>{getInitials(name)}</InitialsCircle>;
  }

  return <ImageCircle src={image} onError={() => setFailedImage(image)} />;
};

export type StackedUser = {
  _id: string;
  name: string;
  image?: string;
};

type UserAvatarStackProps = {
  users: StackedUser[];
  visibleCount: number;
  overflowTooltipText: string;
};

/**
 * Overlapping stack of user profile images, such as the member cell of a group
 * row. Shows the first visibleCount users and closes the stack with a bubble
 * counting the rest.
 *
 * Accessibility note: the circles are marked aria-hidden because a stack shows
 * no names — each user's name is exposed through their tooltip instead, and
 * the full membership is available on the group itself. Colors meet WCAG AA
 * contrast (4.5:1) so the initials stay readable for users with low vision.
 */
export const UserAvatarStack = ({
  users,
  visibleCount,
  overflowTooltipText,
}: UserAvatarStackProps) => {
  const overflowCount = users.length - visibleCount;
  return (
    <FlexBox gap="none" align="center">
      {users.slice(0, visibleCount).map(user => (
        <StackedAvatarCircle key={user._id} data-tooltip-text={user.name}>
          <AvatarContent name={user.name} image={user.image} />
        </StackedAvatarCircle>
      ))}
      {overflowCount > 0 && (
        <StackedAvatarCircle data-tooltip-text={overflowTooltipText}>
          {`+${overflowCount}`}
        </StackedAvatarCircle>
      )}
    </FlexBox>
  );
};

type InlineUserAvatarProps = {
  image: string | undefined;
};

/**
 * Profile image for a user rendered inline with their name or email, in place
 * of the leading person icon of a value decorator.
 *
 * Falls back to the person icon rather than initials, both when no image is
 * set and when the image fails to load: this renders inside tight AG Grid
 * cells where initials would compete with the name beside them.
 */
export const InlineUserAvatar = ({ image }: InlineUserAvatarProps) => {
  const [failedImage, setFailedImage] = useState<string | null>(null);

  if (!image || image === failedImage) {
    return (
      <Icon iconName={IconName.PERSON} iconSize={getDecoratorIconSize()} />
    );
  }

  return (
    <InlineAvatarImage
      aria-hidden="true"
      alt=""
      src={image}
      onError={() => setFailedImage(image)}
    />
  );
};

/**
 * Profile image for the user a surface is about, falling back to initials,
 * sized to sit beside a page heading or in the footer of an asset card. Use
 * UserAvatar for users listed within a page.
 *
 * Accessibility note: marked aria-hidden because the image and the initials
 * are decorative — the heading or footer next to it already names the user.
 * Colors meet WCAG AA contrast (4.5:1) so the initials stay readable for users
 * with low vision.
 */
export const UserProfileAvatar = ({ name, image }: UserAvatarProps) => (
  <ProfileAvatarCircle>
    <AvatarContent name={name} image={image} />
  </ProfileAvatarCircle>
);

const IconAvatarCircle = styled.div.attrs({
  'aria-hidden': 'true',
})<{ $size: string; $backgroundColor: string | undefined }>`
  color: ${colors.textModerate};
  height: ${({ $size }) => $size};
  width: ${({ $size }) => $size};
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  display: flex;
  overflow: hidden;
  flex-shrink: 0;
  ${({ $backgroundColor }) =>
    $backgroundColor
      ? css`
          background: ${$backgroundColor};
        `
      : css`
          box-sizing: border-box;
          background: transparent;
          border: 1px solid ${colors.borderNeutral};
        `}
`;

type IconAvatarSize = Exclude<SizeVariant, 'none'>;

const ICON_AVATAR_CIRCLE_SIZE: Record<IconAvatarSize, string> = {
  s4: spacing.s4,
  s8: spacing.s8,
  s16: spacing.s16,
  s24: spacing.s24,
  s32: spacing.s32,
};

type IconAvatarProps = {
  iconName: IconName;
  color?: string;
  backgroundColor?: string;
  iconSize?: IconSize;
  size: IconAvatarSize;
};

export const IconAvatar = ({
  iconName,
  color,
  backgroundColor,
  iconSize,
  size,
}: IconAvatarProps) => (
  <IconAvatarCircle
    $size={ICON_AVATAR_CIRCLE_SIZE[size]}
    $backgroundColor={backgroundColor}
  >
    <Icon iconName={iconName} color={color} iconSize={iconSize} />
  </IconAvatarCircle>
);

export type AvatarData =
  | { type: 'user'; name: string; image: string | undefined }
  | { type: 'icon'; name: string; iconName: IconName; description: string };
